import { Checkbox, Text, VStack } from '@chakra-ui/react'
import { MultipleSelectField } from '@hb/shared'
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { InputElement, InputProps } from '../../../../types/fields'
import { getInputSizeTokens, useInputSize } from '../Shared/sizes'

export const MultipleSelectInput: InputElement<MultipleSelectField> = forwardRef<
  {
    focus: () => void
  },
  InputProps<MultipleSelectField>
>((props, ref) => {
  const { field, input, disabled, readOnly, style } = props
  const size = useInputSize(props.size ?? field?.size)
  const tokens = getInputSizeTokens(size)
  const { options, renderOption } = field ?? {}
  const { value, onChange } = input ?? {}
  const valueAsArray = useMemo(() => {
    if (typeof value === 'string') {
      return value.split(', ')
    }
    if (Array.isArray(value)) {
      return value
    }
    return []
  }, [value])
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  // Roving tabindex: only one checkbox is tab-reachable at a time, so Tab moves
  // to the next form field rather than stepping through each checkbox. Arrow
  // keys move focus within the group.
  const [focusedIndex, setFocusedIndex] = useState(0)
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRefs.current[focusedIndex]?.focus()
    },
  }))
  const selectedIds = useMemo(
    () => options.filter(o => valueAsArray.includes(o.text)).map(o => o.id),
    [options, valueAsArray],
  )
  const handleChange = useCallback(
    (id: string, checked: boolean) => {
      const updated = checked
        ? Array.from(new Set([...selectedIds, id]))
        : selectedIds.filter((v: string) => v !== id)

      onChange({ target: { value: updated.length ? updated : undefined } })
    },
    [selectedIds, onChange],
  )
  const focusIndex = useCallback((index: number) => {
    setFocusedIndex(index)
    inputRefs.current[index]?.focus()
  }, [])
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>, index: number) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          e.preventDefault()
          focusIndex((index + 1) % options.length)
          break
        }
        case 'ArrowUp':
        case 'ArrowLeft': {
          e.preventDefault()
          focusIndex((index - 1 + options.length) % options.length)
          break
        }
        case ' ':
        case 'Enter': {
          // Toggle manually and preventDefault so the native checkbox doesn't
          // also toggle (which would cancel out our change).
          e.preventDefault()
          if (disabled || readOnly) break
          const o = options[index]
          handleChange(o.id, !selectedIds.includes(o.id))
          break
        }
        default:
          break
      }
    },
    [focusIndex, handleChange, options, selectedIds, disabled, readOnly],
  )
  // Flush vertical checklist — Charting UI Visual Design System (spec Section 0.6.5).
  // No bordered input box: checkboxes sit directly in the section, 8px apart, with 12px
  // #333 labels and an 8px gap between each checkbox and its label.
  return (
    <VStack align="flex-start" spacing={tokens.bottomSpacer} py={1} style={style}>
      {options.map((o, index) => (
        <Checkbox
          key={o.id}
          ref={el => {
            inputRefs.current[index] = el
          }}
          colorScheme="green"
          size={tokens.checkboxSize}
          alignItems="center"
          tabIndex={index === focusedIndex ? 0 : -1}
          onChange={e => handleChange(o.id, e.target.checked)}
          onKeyDown={e => handleKeyDown(e, index)}
          onFocus={() => setFocusedIndex(index)}
          isDisabled={disabled}
          isReadOnly={readOnly}
          isChecked={selectedIds.includes(o.id)}>
          {renderOption ? (
            renderOption(o)
          ) : (
            <Text fontSize={tokens.placeholderFontSize} color="#333333" fontFamily="Hero-New">
              {o.text}
            </Text>
          )}
        </Checkbox>
      ))}
    </VStack>
  )
})
